import type { InAppBrowserType } from "@/lib/utils/in-app-browser";

/**
 * 인앱 별 모바일 화면 모형 SVG. 우측 상단의 메뉴 위치를 빨간 동그라미 + 화살표로 강조.
 * 텍스트만으로는 사용자가 메뉴 위치를 못 찾아 헤매는 문제를 시각적으로 해소.
 *
 * 의도적으로 단순한 도형 모형 — 앱 로고/UI 표절을 피함.
 */
interface Props {
  browserType: InAppBrowserType;
  className?: string;
  /** "여기를 누르세요" 등 강조 라벨 — 다국어용 */
  highlightLabel: string;
}

interface AppPreviewSpec {
  /** 우측 상단 메뉴 아이콘 표시 형태 */
  iconKind: "dots" | "arrow";
  /** 우측 상단 강조할 픽셀 위치 (240×420 viewBox 기준) */
  highlight: { x: number; y: number };
  /** 헤더에 표시할 라벨 (앱 이름 — 영문 그대로) */
  headerLabel: string;
  /** 헤더 배경 색 (앱 브랜드 분위기, 정확한 브랜드 색은 회피) */
  headerColor: string;
}

const SPEC_BY_TYPE: Partial<Record<InAppBrowserType, AppPreviewSpec>> = {
  instagram: {
    iconKind: "dots",
    highlight: { x: 215, y: 32 },
    headerLabel: "instagram.com",
    headerColor: "#262626",
  },
  facebook: {
    iconKind: "dots",
    highlight: { x: 215, y: 32 },
    headerLabel: "facebook.com",
    headerColor: "#1877F2",
  },
  threads: {
    iconKind: "dots",
    highlight: { x: 215, y: 32 },
    headerLabel: "threads.net",
    headerColor: "#000000",
  },
  twitter: {
    iconKind: "arrow",
    highlight: { x: 215, y: 32 },
    headerLabel: "x.com",
    headerColor: "#000000",
  },
  kakaotalk: {
    iconKind: "dots",
    highlight: { x: 215, y: 32 },
    headerLabel: "locarora.com",
    headerColor: "#FEE500",
  },
  line: {
    iconKind: "dots",
    highlight: { x: 215, y: 32 },
    headerLabel: "locarora.com",
    headerColor: "#06C755",
  },
  naver: {
    iconKind: "dots",
    highlight: { x: 215, y: 32 },
    headerLabel: "locarora.com",
    headerColor: "#03C75A",
  },
};

const DEFAULT_SPEC: AppPreviewSpec = {
  iconKind: "dots",
  highlight: { x: 215, y: 32 },
  headerLabel: "browser",
  headerColor: "#262626",
};

export function InAppBrowserAppPreview({
  browserType,
  className,
  highlightLabel,
}: Props) {
  const spec = SPEC_BY_TYPE[browserType] ?? DEFAULT_SPEC;
  const headerTextFill = isLightHeader(spec.headerColor) ? "#000000" : "#FFFFFF";

  return (
    <svg
      role="img"
      aria-label={`${spec.headerLabel} in-app browser top-right menu indicator`}
      viewBox="0 0 240 280"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* 휴대폰 외곽 */}
      <rect
        x="6"
        y="6"
        width="228"
        height="268"
        rx="22"
        ry="22"
        fill="#FFFFFF"
        stroke="#D4D4D8"
        strokeWidth="2"
      />
      {/* 노치 */}
      <rect x="96" y="14" width="48" height="6" rx="3" fill="#D4D4D8" />

      {/* 인앱 브라우저 헤더 */}
      <rect
        x="14"
        y="28"
        width="212"
        height="38"
        rx="6"
        fill={spec.headerColor}
      />
      {/* 닫기(X) 좌측 */}
      <path
        d="M28 47 l8 -8 M28 39 l8 8"
        stroke={headerTextFill}
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* 호스트 라벨 */}
      <text
        x="120"
        y="51"
        textAnchor="middle"
        fontFamily="-apple-system, system-ui, sans-serif"
        fontSize="10"
        fontWeight="600"
        fill={headerTextFill}
      >
        {spec.headerLabel}
      </text>
      {/* 우측 상단 메뉴 아이콘 */}
      {spec.iconKind === "dots" ? (
        <g fill={headerTextFill}>
          <circle cx="207" cy="47" r="2" />
          <circle cx="213" cy="47" r="2" />
          <circle cx="219" cy="47" r="2" />
        </g>
      ) : (
        <g
          stroke={headerTextFill}
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M207 47 l8 -8 l8 8" />
          <path d="M215 39 v14" />
        </g>
      )}

      {/* 본문 placeholder */}
      <rect x="22" y="80" width="196" height="14" rx="3" fill="#E4E4E7" />
      <rect x="22" y="104" width="160" height="10" rx="3" fill="#F4F4F5" />
      <rect x="22" y="122" width="180" height="10" rx="3" fill="#F4F4F5" />
      <rect x="22" y="142" width="196" height="80" rx="6" fill="#F4F4F5" />
      <rect x="22" y="232" width="120" height="10" rx="3" fill="#F4F4F5" />

      {/* 강조: 빨간 원 + 화살표 */}
      <g>
        <circle
          cx={spec.highlight.x}
          cy={spec.highlight.y + 15}
          r="20"
          fill="none"
          stroke="#EF4444"
          strokeWidth="3"
          strokeDasharray="4 3"
        />
        <g
          stroke="#EF4444"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {/* 화살표: 강조 원 아래에서 위로 */}
          <path d="M180 110 L200 65" />
          <path d="M195 62 L200 65 L197 70" />
        </g>
        <text
          x="155"
          y="125"
          textAnchor="end"
          fontFamily="-apple-system, system-ui, sans-serif"
          fontSize="11"
          fontWeight="700"
          fill="#EF4444"
        >
          {highlightLabel}
        </text>
      </g>
    </svg>
  );
}

function isLightHeader(hex: string): boolean {
  // 단순 luminance 근사 — KakaoTalk 노란색은 검정 텍스트가 가독성 좋음.
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 160;
}
